import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import SectionHeading from '../shared/SectionHeading';
import PanelSkeleton from '../shared/skeletons/PanelSkeleton';

import formatters from '../shared/formatters';

import moment from 'moment-timezone';

const DAYS_PER_WEEK = 7;

const headerCellSx = {
  fontSize: '11px',
  fontWeight: 600,
  lineHeight: '14px',
  letterSpacing: '0.04em',
  color: '#A8A49E',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #EAEAEA',
};

const bodyCellSx = {
  fontSize: '12px',
  lineHeight: '16px',
  color: '#1A1916',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #F2F0EC',
};

// Additive weekly fields, mapped to the output column that sums them. Forecast and
// actual weeks name these differently, hence one map per window.
const FORECAST_FIELD_MAP = {revenue: 'revenue', labor: 'laborCost', net: 'grossProfit', hours: 'scheduledHours'};
const ACTUAL_FIELD_MAP = {revenue: 'actualRevenue', labor: 'actualLaborCost', net: 'grossProfit', hours: 'confirmedHours'};

// Distinct headcounts cannot be summed across weeks, so we surface the per-week range
// within the month instead. Only forecast weeks carry these counts.
const HEADCOUNT_FIELDS = {clients: 'activeClientCount', caregivers: 'caregiverCount'};

const formatRange = (range) => {
  if (!range) {
    return formatters.formatCount(null);
  }

  if (range.min === range.max) {
    return formatters.formatCount(range.min);
  }

  return `${formatters.formatCount(range.min)}–${formatters.formatCount(range.max)}`;
};

// Rolls ISO-week buckets into calendar months. Each week's additive totals are split
// across the months its seven days fall in (a boundary week landing 3 days in one
// month contributes 3/7 there), so straddling weeks are attributed proportionally
// rather than dumped whole into one month.
const buildMonthlyRows = (weeks, {ascending, fieldMap, includeHeadcounts}) => {
  const weekKeys = formatters.sortWeekKeysAscending(weeks);

  if (!weekKeys.length) {
    return [];
  }

  const monthsByKey = {};

  weekKeys.forEach((weekKey) => {
    const monday = formatters.weekOfToMoment(weekKey);

    if (!monday) {
      return;
    }

    for (let dayOffset = 0; dayOffset < DAYS_PER_WEEK; dayOffset += 1) {
      const monthKey = monday.clone().add(dayOffset, 'day').format('YYYY-MM');
      const bucket = monthsByKey[monthKey] ||
        (monthsByKey[monthKey] = {monthKey, sums: {}, coveredDays: 0, weekKeys: new Set()});

      bucket.coveredDays += 1;
      bucket.weekKeys.add(weekKey);

      Object.keys(fieldMap).forEach((outKey) => {
        const value = weeks[weekKey]?.[fieldMap[outKey]];

        if (!formatters.isMissingValue(value)) {
          bucket.sums[outKey] = (bucket.sums[outKey] || 0) + value / DAYS_PER_WEEK;
        }
      });
    }
  });

  const monthKeys = Object.keys(monthsByKey).sort((left, right) => left.localeCompare(right));

  if (!ascending) {
    monthKeys.reverse();
  }

  return monthKeys.map((monthKey) => {
    const bucket = monthsByKey[monthKey];
    const monthMoment = moment.utc(monthKey, 'YYYY-MM');
    const daysInMonth = monthMoment.daysInMonth();
    const {revenue, net} = bucket.sums;
    // Margin is a ratio: recompute it from the summed components, never average weeks.
    const margin = (!formatters.isMissingValue(revenue) && revenue > 0 && !formatters.isMissingValue(net)) ?
      net / revenue :
      null;

    let headcounts = null;

    if (includeHeadcounts) {
      const rangeOf = (field) => {
        const values = [...bucket.weekKeys]
            .map((weekKey) => weeks[weekKey]?.[field])
            .filter((value) => !formatters.isMissingValue(value));

        return values.length ? {min: Math.min(...values), max: Math.max(...values)} : null;
      };

      headcounts = {
        clients: rangeOf(HEADCOUNT_FIELDS.clients),
        caregivers: rangeOf(HEADCOUNT_FIELDS.caregivers),
      };
    }

    return {
      monthKey,
      label: monthMoment.format('MMM YYYY'),
      partial: bucket.coveredDays < daysInMonth,
      coveredDays: bucket.coveredDays,
      daysInMonth,
      sums: bucket.sums,
      margin,
      headcounts,
    };
  });
};

const renderMonthCell = (row) => (
  <Stack spacing={0.25}>
    <Typography
      sx={{
        fontSize: '12px',
        fontWeight: 600,
        lineHeight: '16px',
        color: row.partial ? '#A8A49E' : '#1A1916',
      }}
    >
      {row.label}
    </Typography>
    {
      row.partial &&
      <Typography sx={{fontSize: '10px', lineHeight: '13px', color: '#C0BBB2'}}>
        {translate('metrics.ClientMetrics.MonthlySummary.Partial', {covered: row.coveredDays, total: row.daysInMonth})}
      </Typography>
    }
  </Stack>
);

const MONTH_COLUMN = {
  key: 'month',
  align: 'left',
  headerExpression: 'metrics.ClientMetrics.MonthlySummary.Month',
  render: renderMonthCell,
};

const currencyColumn = (key, headerExpression, sumKey) => ({
  key,
  align: 'right',
  headerExpression,
  render: (row) => formatters.formatCurrency(row.sums[sumKey]),
});

const marginColumn = {
  key: 'margin',
  align: 'right',
  headerExpression: 'metrics.ClientMetrics.MonthlySummary.BlendedMargin',
  render: (row) => formatters.formatMargin(row.margin),
};

const headcountColumn = (key, headerExpression, headcountKey) => ({
  key,
  align: 'right',
  headerExpression,
  render: (row) => formatRange(row.headcounts?.[headcountKey]),
});

const FORECAST_COLUMNS = [
  MONTH_COLUMN,
  currencyColumn('revenue', 'metrics.ClientMetrics.MonthlySummary.ClientRevenue', 'revenue'),
  currencyColumn('labor', 'metrics.ClientMetrics.MonthlySummary.Labor', 'labor'),
  currencyColumn('net', 'metrics.ClientMetrics.MonthlySummary.NetRevenue', 'net'),
  {
    key: 'hours',
    align: 'right',
    headerExpression: 'metrics.ClientMetrics.MonthlySummary.ScheduledHours',
    render: (row) => formatters.formatHours(row.sums.hours),
  },
  marginColumn,
  headcountColumn('clients', 'metrics.ClientMetrics.MonthlySummary.Clients', 'clients'),
  headcountColumn('caregivers', 'metrics.ClientMetrics.MonthlySummary.Caregivers', 'caregivers'),
];

const ACTUAL_COLUMNS = [
  MONTH_COLUMN,
  currencyColumn('revenue', 'metrics.ClientMetrics.MonthlySummary.ClientRevenue', 'revenue'),
  currencyColumn('labor', 'metrics.ClientMetrics.MonthlySummary.Labor', 'labor'),
  currencyColumn('net', 'metrics.ClientMetrics.MonthlySummary.NetRevenue', 'net'),
  {
    key: 'hours',
    align: 'right',
    headerExpression: 'metrics.ClientMetrics.MonthlySummary.ConfirmedHours',
    render: (row) => formatters.formatHours(row.sums.hours),
  },
  marginColumn,
];

const MonthlySummarySection = (props) => {
  const {
    isLoading,
    currentSnapshot,
  } = props;

  const {forecastRows, actualRows} = React.useMemo(() => {
    return {
      forecastRows: buildMonthlyRows(currentSnapshot?.forecast?.weeks || {}, {
        ascending: true,
        fieldMap: FORECAST_FIELD_MAP,
        includeHeadcounts: true,
      }),
      // Actuals read most-recent first, matching the weekly breakdown.
      actualRows: buildMonthlyRows(currentSnapshot?.actual?.weeks || {}, {
        ascending: false,
        fieldMap: ACTUAL_FIELD_MAP,
        includeHeadcounts: false,
      }),
    };
  }, [currentSnapshot]);

  const renderTablePanel = (titleExpression, subtitleExpression, columns, rows, emptyExpression) => (
    <Paper
      sx={{
        p: 3,
        borderRadius: '12px',
        border: '1px solid #E5E5E5',
        boxShadow: 'none',
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 600,
          lineHeight: '21px',
          color: '#1A1916',
          mb: 0.25,
        }}
      >
        <Translate expression={titleExpression} />
      </Typography>
      <Typography
        sx={{
          fontSize: '12px',
          lineHeight: '16px',
          color: '#A8A49E',
          mb: 2,
        }}
      >
        <Translate expression={subtitleExpression} />
      </Typography>
      {
        !rows.length &&
        <Alert severity='info'>
          <Translate expression={emptyExpression} />
        </Alert>
      }
      {
        rows.length > 0 &&
        <TableContainer sx={{overflowX: 'auto'}}>
          <Table size='small' sx={{minWidth: 720}}>
            <TableHead>
              <TableRow>
                {
                  columns.map((column) => (
                    <TableCell key={column.key} align={column.align} sx={headerCellSx}>
                      <Translate expression={column.headerExpression} />
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                rows.map((row) => (
                  <TableRow key={row.monthKey}>
                    {
                      columns.map((column) => (
                        <TableCell
                          key={column.key}
                          align={column.align}
                          sx={{...bodyCellSx, color: row.partial ? '#A8A49E' : bodyCellSx.color}}
                        >
                          {column.render(row)}
                        </TableCell>
                      ))
                    }
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        </TableContainer>
      }
    </Paper>
  );

  return (
    <Box>
      <SectionHeading expression='metrics.ClientMetrics.MonthlySummary.SectionTitle' />
      {
        isLoading &&
        <Stack spacing={2}>
          <PanelSkeleton height={220} />
          <PanelSkeleton height={220} />
        </Stack>
      }
      {
        !isLoading &&
        <Stack spacing={2}>
          {
            renderTablePanel(
                'metrics.ClientMetrics.MonthlySummary.ForecastTitle',
                'metrics.ClientMetrics.MonthlySummary.ForecastSubtitle',
                FORECAST_COLUMNS,
                forecastRows,
                'metrics.ClientMetrics.EmptyStates.NoForecastWeeks',
            )
          }
          {
            renderTablePanel(
                'metrics.ClientMetrics.MonthlySummary.ActualTitle',
                'metrics.ClientMetrics.MonthlySummary.ActualSubtitle',
                ACTUAL_COLUMNS,
                actualRows,
                'metrics.ClientMetrics.EmptyStates.NoActualWeeks',
            )
          }
          <Typography
            sx={{
              fontSize: '11px',
              lineHeight: '15px',
              color: '#A8A49E',
            }}
          >
            <Translate expression='metrics.ClientMetrics.MonthlySummary.Note' />
          </Typography>
        </Stack>
      }
    </Box>
  );
};

MonthlySummarySection.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  currentSnapshot: PropTypes.object,
};

export default React.memo(MonthlySummarySection);
